<template>
    <ModalContent stitch fill-height v-bind="modalProps">
        <template slot="title">{{ $translate("title") }}</template>
        <template slot="rawcontent">
            <div
                class="assign-product-catalogs-modal d-flex flex-column min-height-0 h-100"
            >
                <Loading v-if="loading" :absolute="false" />
                <div v-else-if="!catalogs.length" class="assign-product-catalogs-discovery pa-6">
                    <DiscoveryCard
                        :illustration="catalogDiscovery"
                        :loading="creatingCatalog"
                        @choose="createCatalog"
                    />
                </div>
                <div
                    v-else-if="step === 'piece_content'"
                    class="assign-product-catalogs-modal-content d-flex flex-column flex-grow-1 min-height-0 pa-4"
                >
                    <div class="d-flex align-center mb-4">
                        <v-btn text small class="px-0" @click="step = 'select'">
                            <v-icon left small>mdi-arrow-left</v-icon>
                            {{ $translate("buttons.backToSelection") }}
                        </v-btn>
                    </div>
                    <Info :elevation="1" class="mb-4">
                        {{ $translate("messages.pieceContentDescription") }}
                    </Info>
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                :value="pieceContentForm.quantity"
                                :label="$translate('labels.pieceContentQuantity')"
                                :error-messages="pieceContentErrors.quantity"
                                dense
                                outlined
                                hide-details="auto"
                                type="text"
                                inputmode="decimal"
                                @input="
                                    pieceContentForm.quantity =
                                        $utils.normalizeDecimalString($event)
                                "
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="pieceContentForm.unit"
                                :items="pieceContentUnitOptions"
                                :label="$translate('labels.pieceContentUnit')"
                                :error-messages="pieceContentErrors.unit"
                                dense
                                outlined
                                hide-details="auto"
                            />
                        </v-col>
                    </v-row>
                </div>
                <div
                    v-else
                    class="assign-product-catalogs-modal-content d-flex flex-column flex-grow-1 min-height-0"
                >
                    <div class="flex-shrink-0 pt-4 px-4">
                        <div class="text-body-2 mb-4">
                            {{ $translate("messages.instructions") }}
                        </div>
                        <div class="mb-4">
                            <v-btn
                                color="primary"
                                small
                                outlined
                                :loading="creatingCatalog"
                                @click="createCatalog"
                            >
                                <v-icon left small>mdi-plus</v-icon>
                                {{ $translate("buttons.createCatalog") }}
                            </v-btn>
                        </div>
                    </div>
                    <div
                        class="assign-product-catalogs-modal-list flex-grow-1 min-height-0 overflow-y-auto px-4 pb-4"
                    >
                        <v-card
                            v-for="catalog in catalogs"
                            :key="catalog.id"
                            outlined
                            class="assign-catalog-card mb-3"
                            :class="{
                                'assign-catalog-card--active':
                                    isCatalogAssigned(catalog.id),
                            }"
                        >
                            <div class="assign-catalog-card__header px-4 pt-3">
                                <div class="min-width-0">
                                    <div
                                        class="text-subtitle-1 font-weight-medium text-truncate"
                                    >
                                        {{ catalog.name }}
                                    </div>
                                    <div
                                        v-if="isProviderProduct"
                                        class="text-caption grey--text text-truncate"
                                    >
                                        {{ catalogOwnerDisplayName(catalog) }}
                                    </div>
                                </div>
                                <v-chip x-small label class="flex-shrink-0">
                                    {{
                                        catalog.prices_include_vat
                                            ? $translate("labels.inclVat")
                                            : $translate("labels.exclVat")
                                    }}
                                </v-chip>
                            </div>

                            <div class="px-4 pb-2">
                                <!-- Entry rows (default or sale format) -->
                                <template
                                    v-for="(row, rowIndex) in stateFor(
                                        catalog.id
                                    ).rows"
                                >
                                    <v-divider
                                        v-if="rowIndex > 0"
                                        :key="`divider-${catalog.id}-${row.key}`"
                                        class="assign-catalog-card__divider"
                                    />
                                    <div
                                        :key="`row-${catalog.id}-${row.key}`"
                                        class="assign-catalog-card__row"
                                    >
                                        <FormatSelect
                                            :ref="`formatSelect-${catalog.id}-${row.key}`"
                                            :value="row.formatId"
                                            :formats="formats"
                                            :label="$translate('labels.format')"
                                            :unit-option-label="unitOptionLabel"
                                            :show-create-option="canCreateFormat"
                                            variant="sale"
                                            hide-details="auto"
                                            input-class="assign-catalog-card__format"
                                            @input="onRowFormatChange(row, $event)"
                                            @create-format="
                                                createFormatForRow(catalog.id, row)
                                            "
                                        />
                                        <v-text-field
                                            :value="row.price"
                                            :label="unitAmountLabel(catalog)"
                                            type="text"
                                            inputmode="decimal"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            prefix="€"
                                            class="assign-catalog-card__price"
                                            @input="
                                                onRowPriceChange(row, $event)
                                            "
                                        />
                                        <span
                                            class="assign-catalog-card__row-action"
                                        >
                                            <v-btn
                                                icon
                                                small
                                                :title="
                                                    $translate(
                                                        'buttons.removeEntry'
                                                    )
                                                "
                                                @click="
                                                    removeRow(catalog.id, row)
                                                "
                                            >
                                                <v-icon small>
                                                    mdi-delete-outline
                                                </v-icon>
                                            </v-btn>
                                        </span>
                                    </div>
                                </template>

                                <!-- Empty state: no entry yet -->
                                <div
                                    v-if="!stateFor(catalog.id).rows.length"
                                    class="d-flex align-center justify-space-between py-2"
                                >
                                    <span class="text-body-2 grey--text">
                                        {{ $translate("messages.noEntries") }}
                                    </span>
                                    <v-btn
                                        small
                                        outlined
                                        color="primary"
                                        @click="addRow(catalog.id)"
                                    >
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $translate("buttons.add") }}
                                    </v-btn>
                                </div>
                                <!-- Add another entry -->
                                <div v-else class="mt-1 mb-2">
                                    <v-btn
                                        text
                                        small
                                        color="primary"
                                        class="px-1"
                                        @click="addRow(catalog.id)"
                                    >
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $translate("buttons.addEntry") }}
                                    </v-btn>
                                </div>
                            </div>
                        </v-card>
                    </div>
                </div>
            </div>
        </template>
        <template slot="bottom">
            <v-btn
                class="my-2 my-sm-4 w-100 w-sm-auto"
                :disabled="saving"
                @click="onCancel"
            >
                {{ $translate("buttons.cancel") }}
            </v-btn>
            <v-btn
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :color="assignedCount > 0 ? 'primary' : null"
                :disabled="saving"
                :loading="saving"
                @click="onConfirm"
            >
                <v-icon v-if="assignedCount > 0" left>mdi-content-save</v-icon>
                {{ saveText }}
            </v-btn>
        </template>
    </ModalContent>
</template>

<script>
import axios from "@/plugins/axios";
import { mapState, mapActions } from "vuex";
import GlobalMixins from "@/mixins/global";
import DiscoveryCard from "@/components/home/discovery-card.vue";
import Info from "@/components/common/info";
import CatalogModal from "@/modals/catalog.vue";
import FormatModal from "@/modals/format.vue";

// Sentinel for the default (per unit) entry in format selects. A real id is
// required because Vuetify's v-select does not display null selections.
const UNIT_FORMAT = 0;

export default {
    fillHeight: true,
    mixins: [GlobalMixins],
    components: {
        DiscoveryCard,
        Info,
    },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            catalogs: [],
            productCatalogEntries: [],
            formats: [],
            // catalogId -> { rows: [{ key, formatId, price }] }
            // formatId UNIT_FORMAT (0) means the default (per unit) entry.
            catalogStates: {},
            rowUid: 0,
            loading: false,
            saving: false,
            creatingCatalog: false,
            step: "select",
            pieceContentForm: {
                quantity: null,
                unit: null,
            },
            pieceContentErrors: {},
        };
    },
    computed: {
        ...mapState("session", ["current_entity"]),
        product() {
            return this.params?.product || {};
        },
        modalProps() {
            return this.params?.modalProps || { class: "px-0" };
        },
        isProviderProduct() {
            return Boolean(this.params?.product?.provider);
        },
        canCreateFormat() {
            return this.userHasPermission("catalog.add_format");
        },
        pieceContentUnitOptions() {
            return this.$utils.getPieceContentUnitOptions();
        },
        assignedCount() {
            return this.catalogs.filter((catalog) =>
                this.isCatalogAssigned(catalog.id)
            ).length;
        },
        saveText() {
            if (this.step === "piece_content") {
                return this.$translate("buttons.saveAndAssign");
            }
            if (this.assignedCount === 0) {
                if (this.catalogs.length) {
                    return this.$translate("buttons.doNotAssign");
                }
                return this.$translate("buttons.skip");
            }
            return this.$translate("buttons.assign");
        },
        unitOptionLabel() {
            return (
                this.product?.format_data?.name
                || this.$translate("labels.defaultEntry")
            );
        },
        catalogDiscovery() {
            const copy = [
                this.$translate("messages.emptyState"),
                this.$translate("messages.emptyStateDescription"),
                this.$translate("buttons.createCatalog"),
            ];
            return { scene: "shop", tone: "lavender", fr: copy, en: copy };
        },
    },
    methods: {
        ...mapActions("product", ["assignSalesCatalogs"]),
        productId() {
            return this.params?.product?.id || this.params?.productId || null;
        },
        ensureState(catalogId) {
            if (!this.catalogStates[catalogId]) {
                this.$set(this.catalogStates, catalogId, {
                    rows: [],
                });
            }
            return this.catalogStates[catalogId];
        },
        stateFor(catalogId) {
            // Read-only accessor for templates: never mutates state during
            // render. States are initialized in loadData/createCatalog.
            return this.catalogStates[catalogId] || { rows: [] };
        },
        isCatalogAssigned(catalogId) {
            const state = this.catalogStates[catalogId];
            return Boolean(state && state.rows.length > 0);
        },
        unitAmountLabel(catalog) {
            return catalog.prices_include_vat
                ? this.$translate("labels.unitAmountInclVat")
                : this.$translate("labels.unitAmountExclVat");
        },
        catalogOwnerDisplayName(catalog) {
            const ownerData = catalog?.owner_data;
            if (!ownerData) {
                return "—";
            }
            return ownerData.name || ownerData.legal_name || "—";
        },
        saleFormatById(formatId) {
            return this.formats.find((format) => format.id === formatId);
        },
        addRow(catalogId, formatId = UNIT_FORMAT, price = "") {
            // New rows always default to the per unit entry; the user
            // changes the format afterwards if needed.
            const state = this.ensureState(catalogId);
            const row = {
                key: this.rowUid++,
                formatId,
                price,
            };
            state.rows.push(row);
            return row;
        },
        removeRow(catalogId, row) {
            const state = this.ensureState(catalogId);
            state.rows = state.rows.filter((item) => item !== row);
        },
        onRowFormatChange(row, formatId) {
            row.formatId = formatId ?? UNIT_FORMAT;
        },
        onRowPriceChange(row, value) {
            row.price = this.$utils.normalizeDecimalString(value) || "";
        },
        async createFormatForRow(catalogId, row) {
            try {
                const result = await this.showModal({
                    component: FormatModal,
                    params: {},
                });
                if (result && result.id) {
                    if (
                        !this.formats.some(
                            (format) => format.id === result.id
                        )
                    ) {
                        this.formats.push(result);
                    }
                    this.onRowFormatChange(row, result.id);
                }
            } catch (error) {
                if (error) {
                    this.$swal({
                        title: this.$translate("messages.errorCreatingFormat"),
                        html: this.$utils.resultToSwalContent(this, error),
                        type: "error",
                    });
                }
            } finally {
                this.closeFormatSelectMenu(catalogId, row);
            }
        },
        closeFormatSelectMenu(catalogId, row) {
            const ref = this.$refs[`formatSelect-${catalogId}-${row.key}`];
            const select = Array.isArray(ref) ? ref[0] : ref;
            if (select?.closeMenu) {
                select.closeMenu();
            }
        },
        async fetchFormats() {
            try {
                const response = await axios.get("/api/formats/", {
                    params: { limit: 10000 },
                });
                this.formats =
                    response.data?.results || response.data || [];
            } catch (error) {
                console.error("Error loading sale formats:", error);
                this.formats = [];
            }
        },
        async fetchProductCatalogEntries() {
            const productId = this.productId();
            if (!productId) {
                this.productCatalogEntries = [];
                return;
            }
            try {
                const response = await axios.get(
                    `/api/products/${productId}/catalogs/`
                );
                this.productCatalogEntries = response.data || [];
            } catch (error) {
                console.error("Error loading product catalog entries:", error);
                this.productCatalogEntries = [];
            }
        },
        async fetchCatalogs() {
            const listParams = {
                limit: 200,
                offset: 0,
                type: "sell",
                archived: false,
                ordering: "name",
            };
            const providerId = this.params?.product?.provider;
            let catalogs = [];

            if (providerId) {
                const [ownedResponse, providerResponse] = await Promise.all([
                    axios.get("/api/catalogs/", {
                        params: { ...listParams, owned: true },
                    }),
                    axios.get("/api/catalogs/", {
                        params: { ...listParams, provider: providerId },
                    }),
                ]);
                const seen = new Set();
                for (const catalog of [
                    ...(ownedResponse.data?.results || []),
                    ...(providerResponse.data?.results || []),
                ]) {
                    if (catalog?.id != null && !seen.has(catalog.id)) {
                        seen.add(catalog.id);
                        catalogs.push(catalog);
                    }
                }
            } else {
                const response = await axios.get("/api/catalogs/", {
                    params: { ...listParams, owned: true },
                });
                catalogs = (response.data?.results || []).filter((catalog) => {
                    if (!this.current_entity) {
                        return true;
                    }
                    return catalog.owner === this.current_entity.id;
                });
            }

            this.catalogs = catalogs.sort((a, b) =>
                (a.name || "").localeCompare(b.name || "", undefined, {
                    sensitivity: "base",
                })
            );
        },
        initStatesFromEntries() {
            (this.productCatalogEntries || []).forEach((entry) => {
                // Ad hoc sale content entries (custom quantity without
                // format) are not managed by this modal.
                if (!entry.format && entry.sale_content_quantity != null) {
                    return;
                }
                this.addRow(
                    entry.catalog,
                    entry.format || UNIT_FORMAT,
                    entry.unit_amount != null ? String(entry.unit_amount) : ""
                );
            });
            // Catalogs pre-selected by the caller but without fetched entries
            // (e.g. product just created) stay assigned with a default entry.
            (this.params?.initialSelection || []).forEach((catalogId) => {
                const state = this.ensureState(catalogId);
                if (!state.rows.length) {
                    this.addRow(catalogId, UNIT_FORMAT);
                }
            });
        },
        async loadData() {
            this.loading = true;
            try {
                await Promise.all([
                    this.fetchCatalogs(),
                    this.fetchProductCatalogEntries(),
                    this.fetchFormats(),
                ]);
                this.initStatesFromEntries();
                this.catalogs.forEach((catalog) => {
                    this.ensureState(catalog.id);
                });
            } catch (error) {
                this.$swal({
                    title: this.$translate("messages.errorLoadingCatalogs"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            } finally {
                this.loading = false;
            }
        },
        selectedCatalogsNeedPieceContent() {
            if (!this.product?.id) {
                return false;
            }
            return this.catalogs.some((catalog) => {
                const state = this.catalogStates[catalog.id];
                if (!state || !this.isCatalogAssigned(catalog.id)) {
                    return false;
                }
                return state.rows.some(
                    (row) =>
                        row.formatId &&
                        this.$utils.productNeedsPieceContentForAddToCatalog(
                            this.product,
                            row.formatId,
                            this.formats
                        )
                );
            });
        },
        validatePieceContentForm() {
            const errors = {};
            if (!this.selectedCatalogsNeedPieceContent()) {
                this.pieceContentErrors = {};
                return true;
            }
            const qty = this.pieceContentForm.quantity;
            const unit = this.pieceContentForm.unit;
            if (!qty || Number(qty) <= 0) {
                errors.quantity = [
                    this.$translate("errors.pieceContentQuantityRequired"),
                ];
            }
            if (!unit) {
                errors.unit = [
                    this.$translate("errors.pieceContentUnitRequired"),
                ];
            }
            this.pieceContentErrors = errors;
            return Object.keys(errors).length === 0;
        },
        initPieceContentForm() {
            const pieceContent = this.$utils.getPieceContent(this.product);
            this.pieceContentForm = {
                quantity: pieceContent?.quantity || null,
                unit: pieceContent?.unit || null,
            };
            this.pieceContentErrors = {};
        },
        async saveProductPieceContent() {
            const productId = this.productId();
            if (!productId) {
                return false;
            }
            const payload = {
                piece_content_quantity: this.pieceContentForm.quantity,
                piece_content_unit: this.pieceContentForm.unit,
            };
            await axios.patch(`/api/products/${productId}/`, payload);
            Object.assign(this.product, payload, {
                piece_content: {
                    quantity: this.pieceContentForm.quantity,
                    unit: this.pieceContentForm.unit,
                },
            });
            return true;
        },
        buildPayload() {
            const catalogIds = [];
            const catalogConfigs = {};

            for (const catalog of this.catalogs) {
                const state = this.catalogStates[catalog.id];
                if (!state || !this.isCatalogAssigned(catalog.id)) {
                    continue;
                }
                catalogIds.push(catalog.id);

                const entries = [];
                for (const row of state.rows) {
                    const entry = {};
                    if (row.formatId) {
                        entry.format = row.formatId;
                    }
                    if (row.price) {
                        entry.unit_amount = row.price;
                    }
                    entries.push(entry);
                }
                catalogConfigs[catalog.id] = entries;
            }

            return { catalogIds, catalogConfigs };
        },
        onCancel() {
            this.hideModal();
        },
        async onConfirm() {
            if (this.step === "piece_content") {
                if (!this.validatePieceContentForm()) {
                    return;
                }
                this.saving = true;
                try {
                    await this.saveProductPieceContent();
                    this.step = "select";
                    await this.submitAssignment();
                } catch (error) {
                    this.$swal({
                        title: this.$translate(
                            "messages.errorAssigningCatalogs"
                        ),
                        html: this.$utils.resultToSwalContent(this, error),
                        type: "error",
                    });
                } finally {
                    this.saving = false;
                }
                return;
            }

            if (this.selectedCatalogsNeedPieceContent()) {
                this.initPieceContentForm();
                this.step = "piece_content";
                return;
            }

            await this.submitAssignment();
        },
        async submitAssignment() {
            this.saving = true;
            try {
                const { catalogIds, catalogConfigs } = this.buildPayload();
                const response = await this.assignSalesCatalogs({
                    catalogIds,
                    productId: this.productId(),
                    catalogConfigs,
                });
                this.hideModal({
                    result: {
                        saved: true,
                        created: response?.created ?? 0,
                        catalogIds,
                    },
                });
            } catch (error) {
                this.$swal({
                    title: this.$translate("messages.errorAssigningCatalogs"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            } finally {
                this.saving = false;
            }
        },
        async createCatalog() {
            if (this.creatingCatalog) {
                return;
            }
            this.creatingCatalog = true;
            try {
                const result = await this.showModal({
                    component: CatalogModal,
                    params: {
                        ownedOnly: true,
                        catalog: {
                            type: "sell",
                            owner: this.current_entity?.id || null,
                        },
                    },
                });

                if (result && result.id) {
                    await this.fetchCatalogs();
                    await this.$store.dispatch("session/fetchStats", null, {
                        root: true,
                    });

                    const state = this.ensureState(result.id);
                    if (!state.rows.length) {
                        this.addRow(result.id, UNIT_FORMAT);
                    }

                    this.addAlert(
                        this.$translate("messages.catalogCreated"),
                        "success"
                    );
                }
            } catch (error) {
                if (error) {
                    this.$swal({
                        title: this.$translate("messages.errorCreatingCatalog"),
                        html: this.$utils.resultToSwalContent(this, error),
                        type: "error",
                    });
                }
                await this.fetchCatalogs();
            } finally {
                this.creatingCatalog = false;
            }
        },
    },
    created() {
        this.loadData();
    },
    i18n: {
        messages: {
            en: {
                title: "Add to sales catalogs",
                buttons: {
                    cancel: "Cancel",
                    assign: "Assign",
                    doNotAssign: "Do not assign",
                    skip: "Skip this step",
                    createCatalog: "Create catalog",
                    backToSelection: "Back to catalog selection",
                    saveAndAssign: "Save and assign",
                    add: "Add",
                    addEntry: "Add an entry",
                    createFormat: "Create a format",
                    removeEntry: "Remove this entry",
                },
                labels: {
                    defaultEntry: "Per unit",
                    format: "Format",
                    unitAmountExclVat: "Sell price (excl. VAT)",
                    unitAmountInclVat: "Sell price (incl. VAT)",
                    inclVat: "incl. VAT",
                    exclVat: "excl. VAT",
                    pieceContentQuantity: "Piece content quantity",
                    pieceContentUnit: "Piece content unit",
                },
                errors: {
                    pieceContentQuantityRequired:
                        "Piece content quantity is required.",
                    pieceContentUnitRequired: "Piece content unit is required.",
                },
                messages: {
                    emptyState:
                        "Prepare your first sales catalog",
                    emptyStateDescription:
                        "Bring together the products you sell, set their prices and offer a tailored selection to your customers. Start with this product: you can add more products and set prices later.",
                    instructions:
                        "Add entries (per unit or per sale format) to assign the product to a catalog. Prices are optional and can be set later. The price label (excl. or incl. VAT) depends on the catalog settings.",
                    pieceContentDescription:
                        "This product needs a piece content (e.g. bottle size) before it can be added with the selected sale formats.",
                    errorLoadingCatalogs:
                        "An error occurred while loading catalogs.",
                    errorAssigningCatalogs:
                        "An error occurred while assigning the product to catalogs.",
                    errorCreatingCatalog:
                        "An error occurred while creating the catalog.",
                    errorCreatingFormat:
                        "An error occurred while creating the sale format.",
                    catalogCreated: "Catalog created",
                    noEntries: "Not in this catalog",
                },
            },
            fr: {
                title: "Ajouter aux catalogues de vente",
                buttons: {
                    cancel: "Annuler",
                    assign: "Assigner",
                    doNotAssign: "Ne pas assigner",
                    skip: "Passer cette étape",
                    createCatalog: "Créer un catalogue",
                    backToSelection: "Retour à la sélection",
                    saveAndAssign: "Enregistrer et assigner",
                    add: "Ajouter",
                    addEntry: "Ajouter une entrée",
                    createFormat: "Créer un format",
                    removeEntry: "Retirer cette entrée",
                },
                labels: {
                    defaultEntry: "À l'unité",
                    format: "Format",
                    unitAmountExclVat: "Prix de vente (HT)",
                    unitAmountInclVat: "Prix de vente (TTC)",
                    inclVat: "TTC",
                    exclVat: "HT",
                    pieceContentQuantity: "Contenance unitaire",
                    pieceContentUnit: "Unité de contenance",
                },
                errors: {
                    pieceContentQuantityRequired:
                        "La contenance unitaire est obligatoire.",
                    pieceContentUnitRequired:
                        "L'unité de contenance est obligatoire.",
                },
                messages: {
                    emptyState: "Préparez votre premier catalogue de vente",
                    emptyStateDescription:
                        "Regroupez les produits que vous vendez, définissez leurs tarifs et proposez une sélection adaptée à vos clients. Commencez avec ce produit : vous pourrez enrichir le catalogue et renseigner les prix plus tard.",
                    instructions:
                        "Ajoutez des entrées (à l'unité ou par format de vente) pour assigner le produit à un catalogue. Les prix sont optionnels et peuvent être définis plus tard. Le libellé du prix (HT ou TTC) dépend des paramètres du catalogue.",
                    pieceContentDescription:
                        "Ce produit nécessite une contenance unitaire (ex. taille de bouteille) avant d'être ajouté avec les formats de vente sélectionnés.",
                    errorLoadingCatalogs:
                        "Une erreur est survenue lors du chargement des catalogues.",
                    errorAssigningCatalogs:
                        "Une erreur est survenue lors de l'ajout du produit aux catalogues.",
                    errorCreatingCatalog:
                        "Une erreur est survenue lors de la création du catalogue.",
                    errorCreatingFormat:
                        "Une erreur est survenue lors de la création du format de vente.",
                    catalogCreated: "Catalogue créé",
                    noEntries: "Absent de ce catalogue",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.assign-product-catalogs-modal {
    min-height: 0;
}

.assign-product-catalogs-modal-content {
    min-height: 0;
}

.assign-product-catalogs-modal-list {
    min-height: 0;
    -webkit-overflow-scrolling: touch;
}

.assign-catalog-card {
    border-left: 3px solid transparent;
    transition: border-color 0.2s ease;

    &--active {
        border-left-color: var(--v-primary-base);
    }
}

.assign-catalog-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

.assign-catalog-card__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
}

.assign-catalog-card__format {
    flex: 1 1 auto;
    min-width: 0;
}

.assign-catalog-card__price {
    flex: 0 0 auto;
    width: 180px;
}

.assign-catalog-card__row-action {
    flex: 0 0 auto;
    width: 28px;
    display: inline-flex;
    justify-content: center;
}

.assign-catalog-card__divider {
    opacity: 0.6;
}
</style>

<style scoped>
.assign-product-catalogs-discovery {
    width: 100%;
    max-width: 680px;
    margin: auto;
}
.assign-product-catalogs-discovery ::v-deep .discovery-card {
    text-align: center;
}
.assign-product-catalogs-discovery ::v-deep .discovery-card__copy p {
    max-width: none;
}
</style>
